import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import Divider from 'src/components/ui/Divider/Divider';
import { validateEmail, validateWebsite } from 'src/utils/general';
import useCompany, { Company, companyDataPlaceholder, useCompanyLogo } from 'src/services/company';
import LogoHeadSettings from 'src/components/ui/LogoHeadSettings/LogoHeadSettings';
import { useTranslation } from 'react-i18next';
import Input from '../../ui/Input/Input';
import Button, { ButtonToneProps } from '../../ui/Button/Button';
import styles from './CompanySettings.module.scss';
import CountrySelect from '../../ui/CountrySelect/CountrySelect';
import { useCountriesQuery } from '../../../services/utility';

const CompanySettings = () => {
  const { t } = useTranslation();
  const [companyData, setCompanyData] = useState<Company>(companyDataPlaceholder);
  const { company, error, loading, updateCompanyData } = useCompany();
  const useCompanyLogoData = useCompanyLogo();
  const { data } = useCountriesQuery();

  useEffect(() => {
    if (company) {
      setCompanyData(company);
    }
  }, [company, error, loading]);

  const updateValue = (key: string, value: string | null) => {
    if (key) {
      if (['street', 'street2', 'city', 'zip_code', 'country_code', 'country_name'].includes(key)) {
        if (key.includes('country_code')) {
          companyData.address.country_name = data?.find(country => country.code === value)?.name ?? '';
        }
        setCompanyData({
          ...companyData,
          address: { ...companyData.address, [key]: value },
        });
      } else {
        setCompanyData({ ...companyData, [key]: value });
      }
    }
  };

  const updateSocialProfile = (index: number, value: string) => {
    const socialProfiles = companyData.social_profiles.map((profile, i) =>
      i === index ? { ...profile, handle: value } : profile,
    );
    setCompanyData({ ...companyData, social_profiles: socialProfiles });
  };

  const isSaveDisabled = () => {
    if (!companyData) return true;

    const { name, website, email, phone, address } = companyData;

    const websiteIsEmpty = !website || website.trim() === '';
    const websiteIsInvalid = !websiteIsEmpty && !validateWebsite(website);

    return (
      !(name ?? '').trim() ||
      websiteIsInvalid ||
      !(email ?? '').trim() ||
      !validateEmail(email ?? '') ||
      !(phone ?? '').trim() ||
      !(address?.street ?? '').trim() ||
      !(address?.city ?? '').trim() ||
      !(address?.zip_code ?? '').trim()
    );
  };

  return (
    <div className={classNames(styles.root)}>
      <LogoHeadSettings
        logo={useCompanyLogoData.companyLogo}
        onSubmit={f => useCompanyLogoData.updateCompanyLogo(f)}
        className="my-32"
        title={t('settings.company')}
        description={t('settings.update_company_info')}
      />

      <div className={classNames(styles.base, 'items-center')}>
        <span className="typo-text-16--bold text-full col-span-1">{t('settings.public_profile')}</span>
        <Input
          value={companyData.name}
          error={!companyData?.name?.trim()}
          errorMessage={!companyData?.name?.trim() ? t('errors.field_required_error') : ''}
          onChange={e => updateValue('name', e.target.value)}
          label={t('general.name')}
          placeholder=""
          className="grid-cols-1"
        />
        <Input
          value={companyData.website}
          error={!validateWebsite(companyData.website)}
          onChange={e => updateValue('website', e.target.value)}
          label={t('settings.website')}
          placeholder="https://www.example.com"
          className="grid-cols-1"
          icon="link"
        />
      </div>
      <div className={styles.base}>
        <span className="typo-text-16--bold text-full col-span-1 flex flex-col">{t('settings.email')}</span>
        <Input
          error={!companyData?.email?.trim() || !validateEmail(companyData.email)}
          errorMessage={
            !companyData?.email?.trim()
              ? t('errors.field_required_error')
              : !validateEmail(companyData.email)
                ? t('errors.invalid_email_error')
                : ''
          }
          value={companyData.email}
          onChange={e => updateValue('email', e.target.value)}
          placeholder={t('settings.email')}
          className="col-span-1 lg:col-span-2"
        />
      </div>
      <div className={classNames(styles.base, 'items-center')}>
        <span className="typo-text-16--bold text-full col-span-1">{t('settings.phone_number')}</span>
        <Input
          value={companyData.phone}
          error={!companyData?.phone?.trim()}
          errorMessage={!companyData?.phone?.trim() ? t('errors.field_required_error') : ''}
          onChange={e => updateValue('phone', e.target.value)}
          placeholder={t('settings.phone')}
          className="col-span-1 lg:col-span-2"
        />
      </div>
      <Divider className="mt-32 mb-32" />
      <div className={styles.base}>
        <span className="typo-text-16--bold text-full col-span-1">{t('general.address')}</span>
        <Input
          value={companyData.address.street}
          error={!companyData?.address.street?.trim()}
          errorMessage={!companyData?.address.street?.trim() ? t('errors.field_required_error') : ''}
          onChange={e => updateValue('street', e.target.value)}
          placeholder=""
          className="col-span-1 lg:col-span-2"
          label={t('general.street')}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-32 gap-y-16 col-span-1 lg:col-span-2 lg:col-start-2">
          <Input
            value={companyData.address.street2}
            onChange={e => updateValue('street2', e.target.value)}
            placeholder=""
            className="col-span-1"
            label={t('general.street2')}
          />
          <Input
            value={companyData.address.city}
            error={!companyData?.address.city?.trim()}
            errorMessage={!companyData?.address.city?.trim() ? t('errors.field_required_error') : ''}
            onChange={e => updateValue('city', e.target.value)}
            placeholder=""
            className="col-span-1"
            label={t('general.city')}
          />
          <Input
            value={companyData.address.zip_code}
            error={!companyData?.address.zip_code?.trim()}
            errorMessage={!companyData?.address.zip_code?.trim() ? t('errors.field_required_error') : ''}
            onChange={e => updateValue('zip_code', e.target.value)}
            placeholder=""
            className="col-span-1"
            label={t('general.zip_code')}
          />
          <div className="col-span-1">
            <p className="typo-text-14--bold text-faded mb-12">{t('general.country')}</p>
            <CountrySelect
              parent={companyData}
              editable
              isControlled={false}
              handleInputChangeForAdress={(e, field) => {
                if (e.target.value) {
                  updateValue(field, e.target.value);
                }
              }}
            />
          </div>
        </div>
      </div>

      <Divider />
      <div className={classNames(styles.base)}>
        <span className="typo-text-16--bold text-full col-span-1">{t('general.tax_id')}</span>
        <Input
          value={companyData.tax_id}
          onChange={e => updateValue('tax_id', e.target.value)}
          placeholder={t('general.tax_id')}
          className="col-span-1 lg:col-span-2"
        />
      </div>
      <Divider />
      <div className={classNames(styles.base)}>
        <span className="typo-text-16--bold text-full col-span-1">
          {t('settings.social_profiles')}
          <div className="typo-text-12 text-faded mt-4">{t('settings.social_profiles_info')}</div>
        </span>
        <div className="col-span-1 lg:col-span-2 flex flex-col gap-y-16">
          {companyData.social_profiles
            .sort((a, b) => a.type.localeCompare(b.type))
            .map((profile, index) => (
              <div className="flex items-center gap-x-8" key={profile.type}>
                <Input
                  label={profile.type}
                  className="capitalize"
                  value={profile.handle}
                  onChange={e => updateSocialProfile(index, e.target.value)}
                  placeholder={t('settings.social_profile_placeholder')}
                />
              </div>
            ))}
        </div>
      </div>
      <Divider />
      <div className="mt-32 flex items-center justify-end gap-x-12">
        <Button
          title={t('general.save_changes')}
          tone={ButtonToneProps.PRIMARY_BLUE}
          onClick={() => updateCompanyData(companyData)}
          disabled={isSaveDisabled()}
        />
      </div>
    </div>
  );
};
export default CompanySettings;
